import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, API, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AI_PROMPTS } from "@/lib/constants";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";

function renderMarkdown(text) {
  // lightweight markdown: headings, bold, bullets
  const lines = text.split("\n");
  const out = [];
  let list = [];
  const flush = (i) => { if (list.length) { out.push(<ul key={`ul-${i}`} className="my-2 space-y-1">{list}</ul>); list = []; } };
  lines.forEach((ln, i) => {
    const bold = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") ? <strong key={j} className="font-semibold text-[#0B1729]">{p.slice(2, -2)}</strong> : p);
    if (/^#{1,3}\s/.test(ln)) { flush(i); out.push(<h4 key={i} className="mb-1 mt-3 font-display text-sm font-bold text-[#0B1729]">{ln.replace(/^#{1,3}\s/, "")}</h4>); }
    else if (/^\s*[-*]\s/.test(ln)) { list.push(<li key={i} className="flex gap-2 text-sm text-[#1E2D44]/85"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#FFB000]" /><span>{bold(ln.replace(/^\s*[-*]\s/, ""))}</span></li>); }
    else if (/^\s*\d+\.\s/.test(ln)) { list.push(<li key={i} className="flex gap-2 text-sm text-[#1E2D44]/85"><span className="font-semibold text-[#FFB000]">{ln.match(/^\s*(\d+)\./)[1]}.</span><span>{bold(ln.replace(/^\s*\d+\.\s/, ""))}</span></li>); }
    else if (ln.trim() === "") { flush(i); }
    else { flush(i); out.push(<p key={i} className="my-1.5 text-sm leading-relaxed text-[#1E2D44]/85">{bold(ln)}</p>); }
  });
  flush("end");
  return out;
}

export default function AskAmden() {
  const { orgId } = useOrg();
  const { canEdit } = useAuth();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [convoId, setConvoId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const scrollRef = useRef(null);
  const sentInitial = useRef(false);

  const loadConvos = () => api.get(`/ai/conversations?org_id=${orgId}`).then((r) => setConversations(r.data));

  useEffect(() => { if (orgId) loadConvos(); }, [orgId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamText]);

  useEffect(() => {
    if (orgId && location.state?.prompt && !sentInitial.current) {
      sentInitial.current = true;
      send(location.state.prompt);
    }
  }, [orgId]);

  const openConvo = async (id) => {
    setConvoId(id);
    const { data } = await api.get(`/ai/conversations/${id}/messages`);
    setMessages(data.messages);
  };

  const newChat = () => { setConvoId(null); setMessages([]); setStreamText(""); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: `tmp-${Date.now()}` }]);
    setStreaming(true);
    setStreamText("");
    let acc = "";
    let cid = convoId;
    try {
      const token = localStorage.getItem("amden_token");
      const res = await fetch(`${API}/ai/chat?org_id=${orgId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ conversation_id: convoId, message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data:\s?/, "");
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.conversation_id) cid = evt.conversation_id;
            if (evt.delta) { acc += evt.delta; setStreamText(acc); }
          } catch (e) {}
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: acc, id: `a-${Date.now()}` }]);
      setStreamText("");
      if (!convoId && cid) { setConvoId(cid); loadConvos(); }
    } catch (e) {
      toast.error("Unable to reach Ask Amden. Please try again.");
      setMessages((m) => [...m, { role: "assistant", content: "I'm unable to reach the coaching model right now. Please try again shortly.", id: `err-${Date.now()}` }]);
    } finally {
      setStreaming(false);
    }
  };

  const convert = async (content, target) => {
    try {
      await api.post(`/ai/convert?org_id=${orgId}`, { target, text: content });
      toast.success(`Created ${target === "priority" ? "strategic priority" : target}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const hasMessages = messages.length > 0 || streamText;

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-5" data-testid="ask-amden-page">
      {/* History sidebar */}
      <div className="hidden w-64 shrink-0 flex-col lg:flex">
        <Button onClick={newChat} className="mb-3 w-full bg-[#0B1729] text-white hover:bg-[#1E2D44]" data-testid="new-chat-btn">
          <Icons.Plus size={16} className="mr-1.5" /> New conversation
        </Button>
        <div className="amden-card flex-1 overflow-y-auto p-2">
          {conversations.length === 0 && <p className="p-3 text-xs text-[#1E2D44]/40">No conversations yet.</p>}
          {conversations.map((c) => (
            <button
              key={c.id}
              onClick={() => openConvo(c.id)}
              className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${convoId === c.id ? "bg-[#F4F6F9] text-[#0B1729]" : "text-[#1E2D44]/70 hover:bg-[#F4F6F9]"}`}
            >
              <Icons.MessageSquare size={14} className="shrink-0 text-[#FFB000]" />
              <span className="truncate">{c.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Chat */}
      <div className="amden-card flex flex-1 flex-col overflow-hidden p-0">
        <div className="flex items-center gap-3 border-b border-[#E4E8EF] bg-[#0B1729] px-5 py-3.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#FFB000]"><Icons.Sparkles size={16} className="text-[#0B1729]" /></span>
          <div>
            <div className="font-display text-sm font-bold text-white">Ask Amden</div>
            <div className="text-[11px] text-white/45">Your AI business coach · grounded in your live data</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 lg:px-8">
          {!hasMessages ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B1729]"><Icons.Sparkles size={24} className="text-[#FFC24D]" /></span>
              <h3 className="font-display text-xl font-bold text-[#0B1729]">What would you like help with today?</h3>
              <p className="mt-1.5 max-w-md text-sm text-[#1E2D44]/55">Ask about your priorities, performance, missed targets or preparing for a meeting. I use your real platform data and never invent figures.</p>
              <div className="mt-6 flex max-w-lg flex-wrap justify-center gap-2">
                {AI_PROMPTS.map((p) => (
                  <button key={p} onClick={() => send(p)} className="rounded-full border border-[#E4E8EF] bg-white px-3.5 py-1.5 text-sm text-[#1E2D44]/70 transition-colors hover:border-[#FFC24D] hover:text-[#FFB000]" data-testid={`prompt-${p.slice(0, 8)}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto max-w-2xl space-y-5">
              {messages.map((m) => (
                <div key={m.id} className={m.role === "user" ? "flex justify-end" : ""}>
                  {m.role === "user" ? (
                    <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-[#0B1729] px-4 py-2.5 text-sm text-white">{m.content}</div>
                  ) : (
                    <div className="group">
                      <div className="mb-1.5 flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#FFB000]"><Icons.Sparkles size={12} className="text-[#0B1729]" /></span><span className="text-xs font-semibold text-[#0B1729]">Ask Amden</span></div>
                      <div className="rounded-2xl rounded-tl-sm border border-[#E4E8EF] bg-white px-4 py-3">{renderMarkdown(m.content)}</div>
                      {canEdit && (
                        <div className="mt-2 flex items-center gap-1.5 opacity-100 lg:opacity-0 lg:transition-opacity lg:group-hover:opacity-100">
                          <span className="text-[11px] text-[#1E2D44]/40">Convert to:</span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="flex items-center gap-1 rounded-full border border-[#E4E8EF] px-2.5 py-0.5 text-[11px] font-medium text-[#FFB000] hover:bg-[#F4F6F9]" data-testid="convert-response-btn">
                                <Icons.Wand2 size={12} /> Action / Priority / Issue
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => convert(m.content, "action")}><Icons.ListChecks size={14} className="mr-2" /> Action</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => convert(m.content, "priority")}><Icons.Target size={14} className="mr-2" /> Strategic priority</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => convert(m.content, "issue")}><Icons.TriangleAlert size={14} className="mr-2" /> Issue</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {streaming && (
                <div>
                  <div className="mb-1.5 flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-md bg-[#FFB000]"><Icons.Sparkles size={12} className="text-[#0B1729]" /></span><span className="text-xs font-semibold text-[#0B1729]">Ask Amden</span></div>
                  <div className="rounded-2xl rounded-tl-sm border border-[#E4E8EF] bg-white px-4 py-3">
                    {streamText ? renderMarkdown(streamText) : <span className="flex gap-1"><span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" /><span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" style={{ animationDelay: "0.15s" }} /><span className="h-2 w-2 animate-bounce rounded-full bg-[#FFB000]" style={{ animationDelay: "0.3s" }} /></span>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-[#E4E8EF] p-3 lg:p-4">
          <div className="mx-auto flex max-w-2xl items-end gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask Amden anything about your business…"
              rows={1}
              className="max-h-32 min-h-[44px] resize-none bg-white"
              data-testid="ask-input"
            />
            <Button onClick={() => send()} disabled={streaming || !input.trim()} className="h-11 shrink-0 bg-[#FFB000] px-4 text-[#0B1729] hover:bg-[#E69E00]" data-testid="ask-send">
              {streaming ? <Icons.Loader2 className="animate-spin" size={18} /> : <Icons.ArrowUp size={18} />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
